import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Upload, 
  Play, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  FileSpreadsheet,
  Link as LinkIcon,
  Download,
  ArrowLeft,
  RefreshCw,
  Video,
  AlertCircle,
  Zap,
  Music,
  FileAudio,
  Settings,
  RotateCcw,
  ExternalLink,
  StopCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axiosLib from "axios";

// Create axios instance with credentials for cookie-based auth
const axios = axiosLib.create({
  withCredentials: true
});

const TOKEN_KEY = 'prysh_session_token';
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const API = process.env.REACT_APP_BACKEND_URL;

const ShopsyBatch10s = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  
  const [excelFile, setExcelFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [audioType, setAudioType] = useState("voiceover"); // "voiceover" or "custom"
  const [voiceId, setVoiceId] = useState("21m00Tcm4TlvDq8ikWAM");
  const [enableQc, setEnableQc] = useState(false); // QC toggle
  const [maxConcurrent, setMaxConcurrent] = useState(2); // Concurrency setting
  const [isProcessing, setIsProcessing] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [voices, setVoices] = useState([]);
  
  // Fetch available voices
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await axios.get(`${API}/api/voices`);
        if (response.data.voices) {
          setVoices(response.data.voices);
        }
      } catch (error) {
        console.error("Error fetching voices:", error);
      }
    };
    fetchVoices();
  }, []);
  
  // Poll for batch status
  useEffect(() => {
    let interval;
    if (batchId && isProcessing) {
      interval = setInterval(async () => {
        try {
          // Use V2 API
          const response = await axios.get(`${API}/api/batch-v2/${batchId}/status`);
          setBatchStatus(response.data);
          
          if (response.data.status === "completed" || response.data.status === "failed") {
            setIsProcessing(false);
            clearInterval(interval);
            
            if (response.data.completed > 0) {
              toast.success(`Batch completed! ${response.data.completed} videos generated.`);
            } else {
              toast.error("Batch completed. No videos were generated due to processing errors.");
            }
          }
        } catch (error) {
          console.error("Error fetching batch status:", error);
        }
      }, 3000);
    }
    
    return () => clearInterval(interval);
  }, [batchId, isProcessing]);
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setExcelFile(file);
        toast.success(`File selected: ${file.name}`);
      } else {
        toast.error("Please select an Excel file (.xlsx or .xls)");
      }
    }
  };
  
  const handleAudioSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.mp3', '.wav', '.m4a', '.aac'];
      const isValid = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      if (isValid) {
        setAudioFile(file);
        toast.success(`Audio selected: ${file.name}`);
      } else {
        toast.error("Please select an audio file (.mp3, .wav, .m4a, .aac)");
      }
    }
  };
  
  const handleStartBatch = async () => {
    if (!excelFile) {
      toast.error("Please upload an Excel file with product URLs");
      return;
    }
    
    if (audioType === "custom" && !audioFile) {
      toast.error("Please upload a 10-second audio file or switch to AI voiceover");
      return;
    }
    
    setIsProcessing(true);
    setBatchStatus(null);
    
    try {
      const formData = new FormData();
      formData.append("file", excelFile);
      formData.append("voice_id", voiceId);
      formData.append("enable_qc", enableQc);
      formData.append("max_concurrent", maxConcurrent);
      
      if (audioType === "custom" && audioFile) {
        formData.append("audio_file", audioFile);
      }
      
      // Use V2 API
      const response = await axios.post(`${API}/api/batch-v2/start`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.success) {
        setBatchId(response.data.batch_id);
        const qcMsg = enableQc ? " with Quality Check" : "";
        toast.success(`Processing ${response.data.total_urls} URLs${qcMsg} (${maxConcurrent} concurrent)...`);
      }
    } catch (error) {
      console.error("Error starting batch:", error);
      toast.error(error.response?.data?.detail || "Failed to start batch processing");
      setIsProcessing(false);
    }
  };
  
  const handleStopBatch = async () => {
    if (!batchId) return;
    
    try {
      const response = await axios.post(`${API}/api/batch-v2/${batchId}/stop`);
      if (response.data.success) {
        setIsProcessing(false);
        toast.success("Stop signal sent. Tasks will pause at next checkpoint.");
      } else {
        toast.error(response.data.message || "Failed to stop batch");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to stop batch");
    }
  };
  
  const handleResumeBatch = async () => {
    if (!batchId) return;
    
    try {
      const response = await axios.post(`${API}/api/batch-v2/${batchId}/resume`);
      if (response.data.success) {
        setIsProcessing(true);
        toast.success(`Resuming ${response.data.tasks_to_resume} tasks...`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resume batch");
    }
  };
  
  const handleRetryFailed = async () => {
    if (!batchId) return;
    
    try {
      const response = await axios.post(`${API}/api/batch-v2/${batchId}/retry-failed`);
      if (response.data.success) {
        setIsProcessing(true);
        toast.success(`Retrying ${response.data.failed_tasks_retrying} failed tasks...`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to retry");
    }
  };
  
  const getProgressPercentage = () => {
    if (!batchStatus) return 0;
    const total = batchStatus.total_urls || 1;
    const done = (batchStatus.completed || 0) + (batchStatus.failed || 0);
    return Math.round((done / total) * 100);
  };

  const getStepDisplay = (step) => {
    const steps = {
      'created': '📝 Created',
      'scraped': '🔍 Scraping',
      'analyzed': '🧠 Analyzing',
      'concept_generated': '💡 Concept',
      'script_generated': '📜 Script',
      'anchor_images_generated': '🖼️ Images',
      'anchor_qc_done': '✅ Image QC',
      'videos_generated': '🎬 Videos',
      'video_qc_done': '✅ Video QC',
      'audio_processed': '🎵 Audio',
      'stitched': '🎞️ Stitching',
      'uploaded': '☁️ Uploading',
      'completed': '✅ Done'
    };
    return steps[step] || step;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-green-500/5">
      {/* Header */}
      <header className="border-b border-border/50 backdrop-blur-sm bg-background/80 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/projects")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display text-xl font-bold text-foreground flex items-center gap-2">
                Shopsy Batch 10 Sec
                <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">V2</span>
              </h1>
              <p className="text-sm text-muted-foreground">Batch processing with checkpointing & resume</p>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">
          {!batchId ? (
            /* Input Form */
            <motion.div
              key="input-form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Title */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Zap className="w-8 h-8 text-green-500" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-3xl font-bold mb-2">Batch Processing V2</h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Upload an Excel file with up to 100 product URLs. Features concurrent processing, checkpointing, and resume capability.
                </p>
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm">
                  <Video className="w-4 h-4" />
                  <span>2 scenes × 5 seconds = 10 second ads</span>
                </div>
              </div>
              
              {/* Excel Upload */}
              <div className="bg-card rounded-2xl border border-border p-8">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-green-500" />
                  Excel File
                </h3>
                
                <div 
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                    excelFile ? "border-green-500 bg-green-50" : "border-border hover:border-green-500"
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="excel-upload-zone-10s"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {excelFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                      <span className="font-medium text-green-700">{excelFile.name}</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
                      <p className="font-medium mb-1">Drop Excel file here</p>
                      <p className="text-sm text-muted-foreground">or click to browse (.xlsx, .xls)</p>
                    </>
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground mt-3">
                  Excel should have product URLs in the first column (max 100 URLs)
                </p>
              </div>
              
              {/* Audio Settings */}
              <div className="bg-card rounded-2xl border border-border p-8">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Music className="w-5 h-5 text-green-500" />
                  Audio Settings
                </h3>
                
                {/* Audio Type Toggle */}
                <div className="flex gap-4 mb-6">
                  <button
                    onClick={() => setAudioType("voiceover")}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      audioType === "voiceover" 
                        ? "border-green-500 bg-green-50" 
                        : "border-border hover:border-green-300"
                    }`}
                    data-testid="audio-type-voiceover"
                  >
                    <Video className={`w-6 h-6 mx-auto mb-2 ${audioType === "voiceover" ? "text-green-500" : "text-muted-foreground"}`} />
                    <p className="font-medium text-sm">AI Voiceover</p>
                    <p className="text-xs text-muted-foreground">Auto-generated</p>
                  </button>
                  
                  <button
                    onClick={() => setAudioType("custom")}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      audioType === "custom" 
                        ? "border-green-500 bg-green-50" 
                        : "border-border hover:border-green-300"
                    }`}
                    data-testid="audio-type-custom"
                  >
                    <FileAudio className={`w-6 h-6 mx-auto mb-2 ${audioType === "custom" ? "text-green-500" : "text-muted-foreground"}`} />
                    <p className="font-medium text-sm">Custom Audio</p>
                    <p className="text-xs text-muted-foreground">Upload 10s file</p>
                  </button>
                </div>
                
                {audioType === "voiceover" ? (
                  <div className="space-y-4">
                    <label className="block">
                      <span className="text-sm font-medium text-foreground mb-2 block">Voice</span>
                      <select
                        value={voiceId}
                        onChange={(e) => setVoiceId(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-green-500"
                        data-testid="voice-select-10s"
                      >
                        {voices.length > 0 ? (
                          voices.map((voice) => (
                            <option key={voice.voice_id} value={voice.voice_id}>
                              {voice.name}
                            </option>
                          ))
                        ) : (
                          <option value="21m00Tcm4TlvDq8ikWAM">Rachel (Default)</option>
                        )}
                      </select>
                    </label>
                  </div>
                ) : (
                  <div>
                    <input
                      ref={audioInputRef}
                      type="file"
                      accept=".mp3,.wav,.m4a,.aac"
                      onChange={handleAudioSelect}
                      className="hidden"
                    />
                    <div 
                      className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                        audioFile ? "border-green-500 bg-green-50" : "border-border hover:border-green-500"
                      }`}
                      onClick={() => audioInputRef.current?.click()}
                      data-testid="audio-upload-zone"
                    >
                      {audioFile ? (
                        <div className="flex items-center justify-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span className="font-medium text-green-700">{audioFile.name}</span>
                        </div>
                      ) : (
                        <>
                          <FileAudio className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                          <p className="font-medium text-sm">Upload 10-second audio</p>
                          <p className="text-xs text-muted-foreground">.mp3, .wav, .m4a, .aac</p>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Same audio will be used for all videos in this batch
                    </p>
                  </div>
                )}
              </div>
              
              {/* Processing Settings */}
              <div className="bg-card rounded-2xl border border-border p-8">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-green-500" />
                  Processing Settings
                </h3>
                
                {/* Concurrency Setting */}
                <div className="mb-6">
                  <label className="block">
                    <span className="text-sm font-medium text-foreground mb-2 block">
                      Concurrent Processing
                    </span>
                    <select
                      value={maxConcurrent}
                      onChange={(e) => setMaxConcurrent(Number(e.target.value))}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-green-500"
                      data-testid="concurrency-select"
                    >
                      <option value={1}>1 URL at a time (Slowest)</option>
                      <option value={2}>2 URLs at a time</option>
                      <option value={3}>3 URLs at a time</option>
                      <option value={5}>5 URLs at a time (Recommended)</option>
                      <option value={7}>7 URLs at a time</option>
                      <option value={10}>10 URLs at a time (Fastest)</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-2">
                      Higher concurrency = faster but may hit API rate limits
                    </p>
                  </label>
                </div>
                
                {/* QC Mode Toggle */}
                <div className="flex gap-4">
                  <button
                    onClick={() => setEnableQc(false)}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      !enableQc 
                        ? "border-green-500 bg-green-50" 
                        : "border-border hover:border-green-300"
                    }`}
                    data-testid="qc-mode-fast"
                  >
                    <Zap className={`w-6 h-6 mx-auto mb-2 ${!enableQc ? "text-green-500" : "text-muted-foreground"}`} />
                    <p className="font-medium text-sm">⚡ Fast Mode</p>
                    <p className="text-xs text-muted-foreground">No QC (~2 min/vid)</p>
                  </button>
                  
                  <button
                    onClick={() => setEnableQc(true)}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      enableQc 
                        ? "border-green-500 bg-green-50" 
                        : "border-border hover:border-green-300"
                    }`}
                    data-testid="qc-mode-quality"
                  >
                    <CheckCircle className={`w-6 h-6 mx-auto mb-2 ${enableQc ? "text-green-500" : "text-muted-foreground"}`} />
                    <p className="font-medium text-sm">✨ Quality Mode</p>
                    <p className="text-xs text-muted-foreground">With QC (~3 min/vid)</p>
                  </button>
                </div>
                
                {enableQc && (
                  <p className="text-xs text-green-600 mt-3 text-center">
                    QC checks each image/video and auto-regenerates if issues found
                  </p>
                )}
              </div>
              
              {/* Zoho WorkDrive Info */}
              <div className="bg-green-50 rounded-2xl border border-green-200 p-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  <div>
                    <h3 className="font-semibold text-green-800">Zoho WorkDrive Connected</h3>
                    <p className="text-sm text-green-600">
                      Videos will be automatically uploaded to your Zoho WorkDrive folder
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Start Button */}
              <Button
                onClick={handleStartBatch}
                disabled={!excelFile || isProcessing}
                className="w-full py-6 text-lg font-semibold bg-green-500 hover:bg-green-600"
                data-testid="start-batch-btn-10s"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-2" />
                    Start Batch V2 {enableQc ? "(with QC)" : ""}
                  </>
                )}
              </Button>
            </motion.div>
          ) : (
            /* Progress View */
            <motion.div
              key="progress-view"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Status Header */}
              <div className="text-center">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${
                  batchStatus?.status === "completed" 
                    ? "bg-green-500/10" 
                    : batchStatus?.status === "failed"
                    ? "bg-red-500/10"
                    : "bg-green-500/10"
                }`}>
                  {batchStatus?.status === "completed" ? (
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  ) : batchStatus?.status === "failed" ? (
                    <XCircle className="w-8 h-8 text-red-500" />
                  ) : (
                    <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
                  )}
                </div>
                <h2 className="font-display text-2xl font-bold mb-2">
                  {batchStatus?.status === "completed" 
                    ? "Batch Complete!" 
                    : batchStatus?.status === "failed"
                    ? "Batch Failed"
                    : "Processing..."}
                </h2>
                <p className="text-muted-foreground">
                  {batchStatus?.completed || 0} completed, {batchStatus?.failed || 0} failed of {batchStatus?.total_urls || 0} URLs
                </p>
              </div>
              
              {/* Progress Bar */}
              <div className="bg-card rounded-2xl border border-border p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium">Overall Progress</span>
                  <span className="text-sm text-muted-foreground">{getProgressPercentage()}%</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${getProgressPercentage()}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                  <span>Concurrency: {batchStatus?.max_concurrent || 2}</span>
                  <span>Processing: {batchStatus?.processing || 0}</span>
                </div>
              </div>
              
              {/* Per-URL Status */}
              <div className="bg-card rounded-2xl border border-border p-6">
                <h3 className="font-semibold text-lg mb-4">URL Status</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {batchStatus?.tasks?.map((task, idx) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-xl border ${
                        task.status === 'completed' ? 'bg-green-50 border-green-200' :
                        task.status === 'failed' ? 'bg-red-50 border-red-200' :
                        task.status === 'processing' ? 'bg-blue-50 border-blue-200' :
                        'bg-muted/50 border-border'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {task.status === 'completed' ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : task.status === 'failed' ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : task.status === 'processing' ? (
                            <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                          )}
                          <div>
                            <p className="font-medium text-sm">URL {task.url_index + 1}</p>
                            <p className="text-xs text-muted-foreground truncate max-w-xs">{task.url}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-medium">
                            {getStepDisplay(task.current_step)}
                          </p>
                          {task.zoho_link && (
                            <a 
                              href={task.zoho_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-green-600 hover:underline flex items-center gap-1 justify-end mt-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              View
                            </a>
                          )}
                          {task.error && (
                            <p className="text-xs text-red-500 mt-1">{task.error}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-4 flex-wrap">
                {/* Stop Button - only show when processing */}
                {isProcessing && (
                  <Button
                    onClick={handleStopBatch}
                    variant="destructive"
                    className="flex-1"
                    data-testid="stop-batch-btn"
                  >
                    <StopCircle className="w-4 h-4 mr-2" />
                    Stop Batch
                  </Button>
                )}
                
                {batchStatus?.failed > 0 && !isProcessing && (
                  <Button
                    onClick={handleRetryFailed}
                    disabled={isProcessing}
                    variant="outline"
                    className="flex-1"
                    data-testid="retry-failed-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Retry Failed ({batchStatus.failed})
                  </Button>
                )}
                
                {(batchStatus?.status === 'failed' || batchStatus?.status === 'stopped' || (batchStatus?.pending > 0 && !isProcessing)) && (
                  <Button
                    onClick={handleResumeBatch}
                    disabled={isProcessing}
                    className="flex-1 bg-green-500 hover:bg-green-600"
                    data-testid="resume-btn"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Resume Batch
                  </Button>
                )}
                
                <Button
                  onClick={() => {
                    setBatchId(null);
                    setBatchStatus(null);
                    setExcelFile(null);
                    setAudioFile(null);
                  }}
                  variant="outline"
                  className="flex-1"
                  data-testid="new-batch-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  New Batch
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default ShopsyBatch10s;
